/**
 * Provider-aware wording for the mail/files surfaces (2026-09-29).
 *
 * The platform has been able to drive Google Workspace since the Google
 * adapter landed (2026-09-19): `workspace/google/mail.ts` returns a Gmail
 * `webLink` from exactly the same provider-neutral `createDraft`/`MessageRef`
 * contract the Microsoft adapter fills with an OWA link, and the buttons just
 * open whatever URL comes back. So "Reply in Outlook" on a Google firm already
 * opened a correctly-threaded GMAIL draft — it worked, it was just wearing the
 * wrong name.
 *
 * What was missing was any way for the dashboard to KNOW: `workspaceProvider`
 * appeared nowhere in this app. `GET /api/auth/me` now carries it, and this
 * hook turns it into the user-visible word.
 *
 * Nouns, not sentences: call sites compose (`Reply in {mailApp}`) so this
 * stays a vocabulary rather than a string table that has to grow a key for
 * every new button.
 *
 * Fallback is deliberately MICROSOFT, not neutral wording: an absent field
 * (older API) or a null one (firm not connected yet) then renders exactly what
 * the UI said before this change, so nothing regresses for a Microsoft firm
 * and nothing reads oddly on a firm where these buttons cannot work anyway.
 */
import { useContext } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { useMe } from './use-users-me';
import { brandFor, type MailBrand } from '@/lib/brand/mail-brand';

// The vocabulary itself lives in `lib/brand/mail-brand.ts` (pure, hook-free);
// re-exported so existing `brandFor` / `MailBrand` imports from here still work.
export { brandFor, type MailBrand };

export function useMailBrand(): MailBrand {
  // A label must never be the reason a component fails to render. `useMe` is
  // a React Query hook and `useQuery` THROWS when no QueryClientProvider is
  // above it — which is easy to hit in a component test that only cares about
  // some unrelated bit of chrome. So check for a client first and fall back to
  // the default wording when there isn't one, rather than making every future
  // consumer's test set up a QueryClient it does not otherwise need.
  //
  // Both branches call exactly one hook in a fixed order, so the rules of
  // hooks hold: whether a QueryClientProvider is present is a property of the
  // tree above this component and does not change between renders.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  if (!hasQueryClient) return brandFor(null);

  // `useMe` is a shared React Query key (`['users-me']`), so every surface
  // that asks for the brand reuses one cached fetch rather than issuing its
  // own — and a provider change self-heals on the next refetch instead of
  // being frozen into the localStorage session until re-login.
  const { data } = useMe();
  return brandFor(data?.workspaceProvider);
}
